import axios, { AxiosInstance } from 'axios';
import {
  Container,
  ContainerFilters,
  ContainerPaginationParams,
  ContainerFormData,
  ContainerBatchOperation,
  ContainerBatchResult,
  ContainerCreateRequest,
  ApiResponse,
} from '../types/container';
import { PaginatedResponse } from '../types/index';
import { createSuccessResponse, createErrorResponse } from '../utils/responseTransformer';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';
import { WHOLE_USE_MOCK } from './api';
import { mockDataUtil } from './mockDataUtil';

/**
 * Service class for Container operations
 * Supports both Mock data and real API calls
 */
export class ContainerService {
  private static instance: ContainerService;
  
  // Static configuration to control whether to use mock data
  private static useMock: boolean = true;

  /**
   * Get singleton instance
   */
  public static getInstance(): ContainerService {
    if (!ContainerService.instance) {
      ContainerService.instance = new ContainerService();
    }
    return ContainerService.instance;
  }

  /**
   * Set whether to use mock data
   * @param useMock Whether to use mock data
   */
  public static setUseMock(useMock: boolean): void {
    ContainerService.useMock = useMock;
  }

  /**
   * Get current mock usage setting
   */
  public static getUseMock(): boolean {
    return ContainerService.useMock;
  }

  /**
   * Simulate API delay for development
   * @param ms Milliseconds to delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get containers with filtering, pagination and sorting
   * @param filters Filter criteria
   * @param pagination Pagination parameters
   * @returns Promise with paginated container list
   */
  public async getContainers(
    filters: ContainerFilters = {},
    pagination: ContainerPaginationParams = { currentPage: 1, pageSize: 100 }
  ): Promise<ApiResponse<PaginatedResponse<Container>>> {
    try {
      if (ContainerService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Import mock data dynamically to avoid circular dependencies
        const { getMockContainerListResponse } = await import('../mock/containerData');
        return getMockContainerListResponse(filters, pagination);
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.CONTAINERS.QUERY, {
          ...filters,
          "l4Code":"83b32d89-4ae9-4424-af5d-b49613",
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
          sortBy: pagination.sortBy,
          sortOrder: pagination.sortOrder,
        });
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching containers:', error);
      return createErrorResponse(error.message, 'SERVER_ERROR');
    }
  }

  /**
   * Create new container via process API
   * @param containerData Container form data
   * @returns Promise with created container
   */
  public async createContainer(containerData: ContainerFormData): Promise<ApiResponse<Container>> {
    try {
      if (ContainerService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Import mock data dynamically
        const { getMockContainerCreateResponse } = await import('../mock/containerData');
        return getMockContainerCreateResponse(containerData);
      } else {
        // Prepare request data for process API
        const requestData: ContainerCreateRequest = {
          prefixUseSpecCode: containerData.prefixUseSpecCode,
          quantity: containerData.quantity,
          processDefKey: containerData.processDefKey,
          l4Code: containerData.l4Code,
          containerSpecCode: containerData.containerSpecCode
        };

        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.CONTAINERS.CREATE, requestData);
        return response.data;
      }
    } catch (error) {
      console.error('Error creating container:', error);
     return createErrorResponse(error.message, 'SERVER_ERROR');
    }
  }


  /**
   * Batch update container status (enable/disable)
   * @param operation Batch operation details
   * @returns Promise with batch operation result
   */
  public async batchUpdateContainers(operation: ContainerBatchOperation): Promise<ApiResponse<ContainerBatchResult>> {
    try {
      if (ContainerService.useMock && WHOLE_USE_MOCK) {
        // Simulate API delay
        await this.delay(10);

        // Import mock data dynamically
        const { getMockContainerBatchUpdateResponse } = await import('../mock/containerData');
        return getMockContainerBatchUpdateResponse(operation);
      } else {
        // Call real API
        let req = {
          codes: operation.codes,
          status: operation.operation
        }
        const response = await httpClient.post(API_ENDPOINTS.CONTAINERS.CONTAINER_BATCH_UPDATE, req);
        return response.data;
      }
    } catch (error) {
      console.error('Error batch updating containers:', error);
      return createErrorResponse(error.message, 'SERVER_ERROR');
    }
  }

   /**
   * Batch update container status (enable/disable)
   * @param operation Batch operation details
   * @returns Promise with batch operation result
   */
  public async batchPrintContainers(codes, copies): Promise<any> {
    try {
      if (ContainerService.useMock && WHOLE_USE_MOCK) {
        // Import mock data dynamically
        return mockDataUtil.mockRes();
      } else {
        // Call real API
        const response = await httpClient.post(API_ENDPOINTS.CONTAINERS.CONTAINER_PRINT, {codes: codes, copies: copies});
        return response.data;
      }
    } catch (error) {
      console.error('Error batch updating containers:', error);
      return createErrorResponse(error.message, 'SERVER_ERROR');
    }
  }
}

// Export singleton instance
export default ContainerService.getInstance();